import React, { useState } from "react";
import { Button, Divider, Input, List, Modal, Popconfirm, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { labelToValue, useProductRejects } from "../../../../../data/hooks/firestore/useProductRejects";

const { Text } = Typography;

const ManageRejectReasonsModal = ({ open, onCancel }) => {
  const { t } = useTranslation();
  const { reasons, reasonsLoading, addReason, deleteReason } = useProductRejects();
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const previewValue = labelToValue(newLabel);

  const handleAdd = async () => {
    const trimmedLabel = String(newLabel || "").trim();
    if (!trimmedLabel) return;

    const value = labelToValue(trimmedLabel);
    if (!value) return;

    if (reasons.some((r) => r.value === value)) {
      return;
    }

    try {
      setAdding(true);
      await addReason({ value, label: trimmedLabel });
      setNewLabel("");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (index) => {
    try {
      setDeletingIndex(index);
      await deleteReason(index);
    } finally {
      setDeletingIndex(null);
    }
  };

  return (
    <Modal
      title={t("crate_stock.reject_reasons_title")}
      open={open}
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>{t("close")}</Button>}
    >
      <List
        loading={reasonsLoading}
        dataSource={reasons}
        locale={{ emptyText: t("crate_stock.no_reject_reasons") }}
        renderItem={(reason, index) => (
          <List.Item
            actions={[
              <Popconfirm
                key="delete"
                title={t("crate_stock.delete_reason_confirm")}
                onConfirm={() => handleDelete(index)}
                okText={t("crate_stock.yes")}
                cancelText={t("crate_stock.no")}
              >
                <Button icon={<DeleteOutlined />} size="small" danger loading={deletingIndex === index} />
              </Popconfirm>,
            ]}
          >
            <Space direction="vertical" size={0}>
              <Text strong>{reason.label}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {reason.value}
              </Text>
            </Space>
          </List.Item>
        )}
      />

      <Divider style={{ margin: "12px 0" }} />

      <Space direction="vertical" style={{ width: "100%" }} size={4}>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={t("crate_stock.reason_name_placeholder")}
            onPressEnter={handleAdd}
            maxLength={100}
          />
          <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={handleAdd}>
            {t("crate_stock.add_reason")}
          </Button>
        </Space.Compact>
        {previewValue && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t("crate_stock.reason_value_preview")}:{" "}
            <Text code style={{ fontSize: 11 }}>
              {previewValue}
            </Text>
          </Text>
        )}
      </Space>
    </Modal>
  );
};

export default ManageRejectReasonsModal;
